import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import AdminLogin from './components/AdminLogin';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Providers from './pages/Providers';
import Clients from './pages/Clients';
import Apps from './pages/Apps';
import AppConfig from './pages/AppConfig';
import APIKeys from './pages/APIKeys';
import SecurityCentre from './pages/SecurityCentre';
import Deliverability from './pages/Deliverability';
import EmailPacks from './pages/EmailPacks';
import SecureVault from './pages/SecureVault';
import Templates from './pages/Templates';
import OTPSettings from './pages/OTPSettings';
import ScheduledEmails from './pages/ScheduledEmails';
import Logs from './pages/Logs';
import Queue from './pages/Queue';
import Help from './pages/Help';
import GuidedSetup from './pages/GuidedSetup';
import SimpleConnect from './pages/SimpleConnect';
import PublicForms from './pages/PublicForms';
import Audit from './pages/Audit';
import Usage from './pages/Usage';
import SuppressionList from './pages/SuppressionList';
import AIAssistant from './pages/AIAssistant';
import Settings from './pages/Settings';
import DataModel from './pages/DataModel';
import GenericPage from './pages/GenericPage';
import { navigationConfig } from './config/navigation';
import { gatewayApi, getStoredSession, storeSession } from './services/gateway';
import './App.css';

function App() {
  const [authState, setAuthState] = useState('checking');
  const [admin, setAdmin] = useState(null);
  const [backendStatus, setBackendStatus] = useState({
    gateway: 'checking',
    pocketbase: 'checking'
  });

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      try {
        const health = await gatewayApi.health();
        if (active) setBackendStatus(health.data || {});
      } catch {
        if (active) setBackendStatus({ gateway: 'unreachable', pocketbase: 'unknown' });
      }

      const token = getStoredSession();
      if (!token) {
        if (active) setAuthState('unauthenticated');
        return;
      }

      try {
        const session = await gatewayApi.session();
        const verified = await gatewayApi.checkAdmin();
        if (!active) return;
        setAdmin({...session.data.admin,...verified.data});
        setAuthState('authenticated');
      } catch {
        storeSession('');
        if (active) setAuthState('unauthenticated');
      }
    };

    bootstrap();
    return () => { active = false; };
  }, []);

  const handleAuthenticated = (adminUser) => {
    setAdmin(adminUser);
    setAuthState('authenticated');
  };

  const handleLogout = async () => {
    await gatewayApi.logout();
    setAdmin(null);
    setAuthState('unauthenticated');
  };

  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-600 text-white font-black text-xl flex items-center justify-center mx-auto animate-pulse">B</div>
          <p className="text-sm text-slate-400 mt-4">Validating secure session...</p>
        </div>
      </div>
    );
  }

  if (authState !== 'authenticated') {
    return <AdminLogin backendStatus={backendStatus} onAuthenticated={handleAuthenticated} />;
  }

  const implementedPaths = [
    '/', '/clients', '/apps', '/app-config', '/providers',
    '/api-keys', '/security', '/deliverability', '/email-packs',
    '/secure-vault', '/templates', '/otp-settings', '/scheduled',
    '/logs', '/queue', '/audit', '/usage', '/suppression', '/ai-assistant',
    '/settings', '/data-model', '/help', '/guided-setup', '/connect-app', '/public-forms'
  ];

  return (
    <ErrorBoundary>
      <HashRouter>
        <Routes>
          <Route path="/" element={<Layout admin={admin} backendStatus={backendStatus} onLogout={handleLogout} />}>
            <Route index element={<Dashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="apps" element={<Apps />} />
            <Route path="app-config" element={<AppConfig />} />
            <Route path="providers" element={<Providers />} />
            <Route path="api-keys" element={<APIKeys />} />
            <Route path="security" element={<SecurityCentre />} />
            <Route path="deliverability" element={<Deliverability />} />
            <Route path="email-packs" element={<EmailPacks />} />
            <Route path="secure-vault" element={<SecureVault />} />
            <Route path="templates" element={<Templates />} />
            <Route path="otp-settings" element={<OTPSettings />} />
            <Route path="scheduled" element={<ScheduledEmails />} />
            <Route path="logs" element={<Logs />} />
            <Route path="queue" element={<Queue />} />
            <Route path="audit" element={<Audit />} />
            <Route path="usage" element={<Usage />} />
            <Route path="suppression" element={<SuppressionList />} />
            <Route path="ai-assistant" element={<AIAssistant />} />
            <Route path="settings" element={<Settings />} />
            <Route path="data-model" element={<DataModel />} />
            <Route path="help" element={<Help />} />
            <Route path="guided-setup" element={<GuidedSetup />} />
            <Route path="connect-app" element={<SimpleConnect />} />
            <Route path="public-forms" element={<PublicForms />} />

            {navigationConfig
              .filter(nav => !implementedPaths.includes(nav.path))
              .map((nav) => (
                <Route
                  key={nav.path}
                  path={nav.path.replace('/', '')}
                  element={<GenericPage title={nav.name} />}
                />
              ))}

            <Route path="*" element={<GenericPage title="Page Not Found" />} />
          </Route>
        </Routes>
      </HashRouter>
    </ErrorBoundary>
  );
}

export default App;